import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./institutionPortfolio.css";
export function PortfolioOverlay({
  title,
  close,
  children,
  busy = false,
  centered = false,
}: {
  title: string;
  close: () => void;
  children: ReactNode;
  busy?: boolean;
  centered?: boolean;
}) {
  const panel = useRef<HTMLDivElement>(null),
    onClose = useRef(close);
  onClose.current = close;
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.current?.querySelector<HTMLButtonElement>("button")?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (before?.isConnected) before.focus();
    };
  }, []);
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      const dialogs = document.querySelectorAll('[aria-modal="true"]');
      if (dialogs[dialogs.length - 1] !== panel.current) return;
      if (event.key === "Escape" && !busy) {
        event.preventDefault();
        onClose.current();
      }
      if (event.key === "Tab") {
        const nodes = Array.from(
          panel.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"]',
          ) || [],
        );
        if (!nodes.length) return;
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [busy]);
  return createPortal(
    <div className="institution-portfolio portfolio-overlay">
      <div
        className="portfolio-overlay__backdrop"
        onClick={() => !busy && close()}
      />
      <div
        ref={panel}
        className={
          centered
            ? "portfolio-overlay__panel portfolio-overlay__panel--centered"
            : "portfolio-overlay__panel"
        }
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <h2>{title}</h2>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={busy}
            onClick={close}
            aria-label={`Close ${title}`}
          >
            Close
          </button>
        </header>
        <div className="portfolio-overlay__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
export function PortfolioFilePreview({
  url,
  name,
  mimeType,
  close,
  centered,
}: {
  url: string;
  name: string;
  mimeType: string;
  close: () => void;
  centered?: boolean;
}) {
  return (
    <PortfolioOverlay title={name} close={close} centered={centered}>
      {mimeType.startsWith("image/") ? (
        <img src={url} alt={name} style={{ maxWidth: "100%" }} />
      ) : mimeType === "application/pdf" ? (
        <iframe
          src={url}
          title={name}
          style={{ width: "100%", height: "75vh", border: 0 }}
        />
      ) : (
        <p>This file cannot be previewed. Use Download to open it.</p>
      )}
    </PortfolioOverlay>
  );
}
